
import { Component, Vue, Watch } from "vue-property-decorator"
import { namespace, Getter, State } from "vuex-class"
import { Portal } from "portal-vue"

import CaseList from "@/components/New/Cases/Overview/CaseList.vue"
import ExpandableSidebar from "@/components/New/Page/ExpandableSidebar.vue"

import { ViewsService } from "@/services/viewsService"

import { User } from "@/types"
import { ViewFullSummary, ViewSummary } from "@/types/views/viewSummary"
import ViewFolders from "@/components/New/Folders/ViewFolders.vue"
import { Folder, FolderType, FolderItem } from "@/types/folder"
import api from "@/services/api"
import DataCache from "@/caching/caching"
import { CasesTable } from "@/types/case"
import { CurrentPageDetails, GetCasesForViewRequest } from "@/types/paginatedRequest"
import { TABLE_RESULTS_LOADING_INDICATOR_TIMEOUT_MS } from "@/constants/insights"

const caseStore = namespace("cases")
const setupStore = namespace("setup")

const OVERVIEW_ROUTE_NAME = "v2-cases-overview"

@Component({
	components: {
		"case-list": CaseList,
		"portal": Portal,
		"sidebar": ExpandableSidebar,
		"view-folders": ViewFolders
	}
})
export default class Overview extends Vue {
	@State("userCache") private readonly userCache!: DataCache<User>;
	@Getter("getMe") private readonly me!: User;
	@Getter("getRecordName") private readonly recordName!: string;

	@caseStore.State private viewsService!: ViewsService;
	@caseStore.Action private casesLoad!: () => void;
	@caseStore.Action private caseClear!: () => void;
	@caseStore.Action("detailsLoad") private detailsLoad!: () => void;
	@caseStore.Mutation("setActiveViewId") private setActiveViewId!: (viewId: string | null) => void;

	@setupStore.Action private loadFieldsIfUnloaded!: () => Promise<void>;

	private viewsSearchFilter: string = "";
	private caseSearchFilter: string = "";
	private appliedCaseSearchFilter: string = "";
	private folders: Folder[] = []

	private isMounted: boolean = false
	private loadingPage: boolean = false;
	private cacheId: string = "";
	private pendingTableResultsRequestsByTable: { [tableId: string]: number } = {};
	private paginationLoadingIndicatorTimeouts: { [tableId: string]: number } = {};
	private visiblePaginationLoadingTableIds: string[] = [];
	private latestTableResultsRequestIds: { [tableId: string]: number } = {};
	private skipNextViewLoad: boolean = false;

	/**
	 * @summary Mounted lifecycle hook.
	 */
	private async mounted (): Promise<void> {
		this.caseClear()
		this.detailsLoad()

		const routeViewId = this.$route.params.viewId ?? null
		if (routeViewId) {
			this.viewsService.activeViewId = routeViewId
		}

		const loadUsersPromise = this.userCache.fetchIfNeeded()
		const loadFieldsPromise = this.loadFieldsIfUnloaded()
		const loadFoldersPromise = this.loadFolders()
		const loadViewsPromise: Promise<void> = this.setCases()

		await Promise.allSettled([loadUsersPromise, loadFieldsPromise, loadFoldersPromise, loadViewsPromise])

		if (!routeViewId) {
			this.syncRouteToSelectedView()
		}

		this.isMounted = true
	}

	/**
	 * @summary Puts the view we were given on load into the url, so the breadcrumb and the
	 * sidebar show it the same way they would for a view the user picked themselves.
	 */
	private syncRouteToSelectedView (): void {
		const selectedViewId = this.viewsService.activeViewId

		// The user could have picked a view or left the page while the first load was in flight.
		if (!selectedViewId || this.$route.name !== OVERVIEW_ROUTE_NAME || this.$route.params.viewId) {
			return
		}

		// Its cases are already loaded, so don't let the route change load them a second time.
		this.skipNextViewLoad = true

		const clearSkip = (): void => {
			// Runs after the route watcher has had its turn, so the flag can never be left
			// set and swallow the next view the user picks.
			this.$nextTick(() => {
				this.skipNextViewLoad = false
			})
		}

		this.$router.replace(
			{
				name: OVERVIEW_ROUTE_NAME,
				params: { viewId: selectedViewId },
				query: this.$route.query
			},
			clearSkip,
			clearSkip
		)
	}

	private beforeDestroy (): void {
		Object.keys(this.paginationLoadingIndicatorTimeouts).forEach(tableId => {
			clearTimeout(this.paginationLoadingIndicatorTimeouts[tableId])
		})
		this.paginationLoadingIndicatorTimeouts = {}
	}

	/**
	 * Start the loading indicator for a table's results if they're taking a while.
	 * @param tableId The ID of the table to start the loading indicator for.
	 */
	private startTableResultsLoadingIndicator (tableId: string): void {
		const pendingCount = (this.pendingTableResultsRequestsByTable[tableId] ?? 0) + 1
		Vue.set(this.pendingTableResultsRequestsByTable, tableId, pendingCount)

		if (pendingCount !== 1) {
			return
		}

		const existingTimeout = this.paginationLoadingIndicatorTimeouts[tableId]
		if (existingTimeout !== undefined) {
			clearTimeout(existingTimeout)
		}

		const timeout = window.setTimeout(() => {
			if ((this.pendingTableResultsRequestsByTable[tableId] ?? 0) > 0 && !this.visiblePaginationLoadingTableIds.includes(tableId)) {
				this.visiblePaginationLoadingTableIds = [...this.visiblePaginationLoadingTableIds, tableId]
			}
			Vue.delete(this.paginationLoadingIndicatorTimeouts, tableId)
		}, TABLE_RESULTS_LOADING_INDICATOR_TIMEOUT_MS)

		Vue.set(this.paginationLoadingIndicatorTimeouts, tableId, timeout)
	}

	/**
	 * Stop the loading indicator for a table's results.
	 * @param tableId The ID of the table to stop the loading indicator for.
	 */
	private stopTableResultsLoadingIndicator (tableId: string): void {
		const pendingCount = (this.pendingTableResultsRequestsByTable[tableId] ?? 0) - 1
		if (pendingCount > 0) {
			Vue.set(this.pendingTableResultsRequestsByTable, tableId, pendingCount)
		} else {
			Vue.delete(this.pendingTableResultsRequestsByTable, tableId)
		}

		if ((this.pendingTableResultsRequestsByTable[tableId] ?? 0) > 0) {
			return
		}

		const timeout = this.paginationLoadingIndicatorTimeouts[tableId]
		if (timeout !== undefined) {
			clearTimeout(timeout)
			Vue.delete(this.paginationLoadingIndicatorTimeouts, tableId)
		}

		if (this.visiblePaginationLoadingTableIds.includes(tableId)) {
			this.visiblePaginationLoadingTableIds = this.visiblePaginationLoadingTableIds.filter(id => id !== tableId)
		}
	}

	/**
	 * @summary Gets the summary for the currently selected view.
	 */
	private get viewSummary (): ViewFullSummary | null {
		return this.viewsService.viewSummary
	}

	private get casesTables (): CasesTable[] {
		return this.viewSummary?.casesTables ?? []
	}

	/**
	 * @summary filters the views based on the provided filter term and returns the result as a FolderItem[]
	 */
	private get filteredViews (): FolderItem[] | undefined {
		const lowerFilter = this.viewsSearchFilter ? this.viewsSearchFilter.toLowerCase() : ""
		const filtered = this.viewsSearchFilter
			? this.viewSummary?.summary?.filter(i => i.title.toLowerCase().includes(lowerFilter))
			: this.viewSummary?.summary

		return filtered?.map((fv: ViewSummary) => {
			return {
				...fv,
				itemCount: fv.caseCount
			}
		}) as FolderItem[]
	}

	/**
	 * @summary Gets the number of cases in the current view.
	 */
	private get totalRecordsAvailable (): number {
		return this.casesTables?.reduce((acc, current) => {
			return acc + current.totalRecords
		}, 0) ?? 0
	}

	private totalRecordsShown (): number {
		return this.casesTables?.reduce((acc, current) => {
			return acc + (current.cases?.length ?? 0)
		}, 0) ?? 0
	}

	/**
	 * @summary Gets the title for the currently selected view.
	 */
	private get viewTitle (): string {
		return this.viewSummary?.activeView?.title ?? "Records"
	}

	/**
	 * @summary Gets the text to use when displaying the number of records help text.
	 */
	private get caseTotalText (): string {
		const casesTotal = this.totalRecordsAvailable
		const casesShown = this.totalRecordsShown()
		const hasAppliedCaseFilter = /\S/.test(this.appliedCaseSearchFilter)
		const record: string = "record"
		const records: string = "records"

		if (this.casesTables.length > 0) {
			if (hasAppliedCaseFilter) {
				const recordString = casesTotal === 1 ? record : records
				return `Showing ${casesShown} of ${casesTotal} ${recordString} matching your filter`
			}

			const recordString = casesTotal === 1 ? record : records
			return `${casesTotal} ${recordString}`
		}

		return ""
	}

	// Retrieve all folders
	private async loadFolders (): Promise<void> {
		this.folders = await api.foldersGet(FolderType.View)
	}

	/**
	 * @summary Watcher fired when the selected view is changed via the route.
	 */
	@Watch("$route.params.viewId")
	private async onViewChanged (newViewId: string | null): Promise<void> {
		if (!newViewId) {
			return
		}

		if (this.skipNextViewLoad) {
			this.skipNextViewLoad = false
			return
		}

		this.cacheId = ""
		this.caseSearchFilter = ""
		this.appliedCaseSearchFilter = ""
		this.viewsService.activeViewId = newViewId

		await this.setCases()
	}

	private async setCases (searchTerm: string = ""): Promise<void> {
		await this.viewsService.loadViews(searchTerm, this.cacheId)
		this.appliedCaseSearchFilter = searchTerm.trim()
		this.cacheId = this.viewsService.viewSummary?.id ?? ""
	}

	private async loadNextPage (details: CurrentPageDetails): Promise<void> {
		if (this.viewsService.activeViewId) {
			const tableId = details.tableId ? details.tableId : details.value
			const requestId = (this.latestTableResultsRequestIds[tableId] ?? 0) + 1
			Vue.set(this.latestTableResultsRequestIds, tableId, requestId)
			try {
				this.startTableResultsLoadingIndicator(tableId)
				const request: GetCasesForViewRequest = {
					viewId: this.viewsService.activeViewId,
					page: details.pageNumber ?? 1,
					pageSize: details.pageSize ?? 20,
					searchTerm: details.searchTerm,
					timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
					sortBy: details.sortBy,
					sortDesc: details.sortDesc,
					sortDataType: details.sortDataType,
					tableId: details.tableId,
					id: this.cacheId || this.viewSummary?.id || ""
				}

				const cases = await api.casesForView(request)
				if (requestId === this.latestTableResultsRequestIds[tableId]) {
					const tableToUpdate = this.casesTables.firstOrDefault((c: CasesTable) => c.value === tableId)

					if (tableToUpdate) {
						tableToUpdate.totalRecords = cases.totalItems
						tableToUpdate.cases = [...cases.data]
					}
				}
			} catch (err) {
				console.error(err)
				if (requestId === this.latestTableResultsRequestIds[tableId]) {
					await this.setCases()
				}
			} finally {
				this.stopTableResultsLoadingIndicator(tableId)
			}
		}
	}

	private async reloadInsightBySearch (searchTerm: string): Promise<void> {
		if (this.viewsService.activeViewId) {
			try {
				this.loadingPage = true
				await this.setCases(searchTerm)
			} catch (err) {
				console.error(err)
				this.setCases()
			} finally {
				this.loadingPage = false
			}
		}
	}
}
